/**
 * SmartTask V2 - Workflow Engine
 * 
 * The brain of SmartTask. This is a pure state machine that:
 * 1. Enforces operational rules (GPS, photos, signatures, geofence)
 * 2. Validates state transitions
 * 3. Blocks actions that violate rules
 * 4. Returns clear error messages when rules are violated
 * 
 * All rule enforcement is centralized HERE, not scattered across UI components.
 */

import type {
  WorkflowState,
  WorkflowStateRecord,
  SmartTaskConfig,
  OperationalRules,
  GPSLocation,
  GPSStatus,
  ActionResult,
  WorkItem,
  JobPhoto,
  SignatureRecord,
  ChecklistItem,
  DispatchedRoute,
} from '../types';
import {
  type AppointmentCompletionRequirementKey,
  type ConfigurableTypeOption,
  getAppointmentCompletionRequirementOverride,
  getAppointmentCompletionRequirementKeys,
  getAppointmentCompletionRulesOverride,
} from '../utils/appointmentTypeRules';
import { requiresPhotosForCompletionResolution, requiresSignatureForCompletionResolution, type CompletionResolution } from '../utils/completionResolution';
import { createLogger } from '@/lib/debugLogger';
const logger = createLogger('workflowEngine');

// ============================================================================
// HELPER: Haversine distance (metres) between two GPS points
// ============================================================================

function haversineDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6_371_000; // Earth radius in metres
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

const VALID_TRANSITIONS: Record<WorkflowState, WorkflowState[]> = {
  idle: ['vehicle_required', 'inspection_pending', 'ready', 'traveling'],
  vehicle_required: ['inspection_pending', 'ready', 'idle', 'checked_in'], // Vehicle gates travel only; check-in without travel allowed
  inspection_pending: ['ready', 'idle', 'checked_in', 'paused', 'completing'], // Travel blocked; check-in/pause/complete allowed
  ready: ['traveling', 'checked_in', 'idle'],
  traveling: ['arrived', 'travel_paused', 'ready'],
  travel_paused: ['traveling', 'arrived', 'ready', 'idle'],
  arrived: ['checked_in', 'traveling'],
  checked_in: ['paused', 'completing'],
  paused: ['checked_in', 'completing'],
  completing: ['idle', 'ready'],
};

// Human-readable state labels
export interface CompletionRequirementState {
  key: string;
  label: string;
  met: boolean;
  required: boolean;
  overridden?: boolean;
  overrideReason?: string | null;
}

export const WORKFLOW_STATE_LABELS: Record<WorkflowState, string> = {
  idle: 'Ready',
  vehicle_required: 'Select Vehicle',
  inspection_pending: 'Inspection Required',
  ready: 'Ready to Go',
  traveling: 'On Route',
  travel_paused: 'Travel Paused',
  arrived: 'At Site — Not Checked In',
  checked_in: 'Checked In — Working',
  paused: 'Paused',
  completing: 'Completing',
};

// State colors for UI
export const WORKFLOW_STATE_COLORS: Record<WorkflowState, string> = {
  idle: '#6b7280',
  vehicle_required: '#ef4444',
  inspection_pending: '#f97316',
  ready: '#3b82f6',
  traveling: '#8b5cf6',
  travel_paused: '#d97706',
  arrived: '#06b6d4',
  checked_in: '#10b981',
  paused: '#eab308',
  completing: '#22c55e',
};

// WORKFLOW ACTIONS
// ============================================================================

export type WorkflowAction =
  | 'select_vehicle'
  | 'vehicle_actions'
  | 'complete_inspection'
  | 'cancel_inspection'
  | 'start_travel'
  | 'pause_travel'
  | 'resume_travel'
  | 'cancel_travel'
  | 'arrive_site'
  | 'check_in'
  | 'check_in_without_travel'
  | 'pause_work'
  | 'resume_work'
  | 'start_completing'
  | 'complete_job'
  | 'record_child_entry'
  | 'take_photo'
  | 'get_signature'
  | 'scan_qr'
  // Visitor management — handled as modal actions in SmartTaskShell before
  // reaching the state machine (not state transitions).
  | 'visitor_arrival'
  | 'visitor_departure';

/** What actions are available in each state */
const STATE_ACTIONS: Record<WorkflowState, WorkflowAction[]> = {
  idle: ['select_vehicle', 'start_travel', 'check_in_without_travel'],
  vehicle_required: ['select_vehicle', 'check_in_without_travel'],
  inspection_pending: ['complete_inspection', 'cancel_inspection', 'check_in', 'check_in_without_travel', 'take_photo', 'get_signature', 'record_child_entry', 'scan_qr'],
  ready: ['start_travel', 'check_in', 'check_in_without_travel', 'take_photo'],
  traveling: ['arrive_site', 'pause_travel', 'cancel_travel', 'take_photo'],
  travel_paused: ['resume_travel', 'arrive_site', 'cancel_travel'],
  arrived: ['check_in', 'take_photo'],
  checked_in: ['pause_work', 'start_completing', 'take_photo', 'get_signature', 'record_child_entry', 'scan_qr'],
  paused: ['resume_work', 'start_completing'],
  completing: ['complete_job', 'take_photo', 'get_signature'],
};

// ============================================================================
// RULE ENFORCEMENT ENGINE
// ============================================================================

export interface RuleContext {
  gpsLocation: GPSLocation | null;
  gpsStatus: GPSStatus;
  clockedIn: boolean;
  hasVehicle: boolean;
  hasInspectionToday: boolean;
  /** True if there is ANY previous inspection for the current vehicle (regardless of date) */
  hasInspectionForVehicle: boolean;
  currentPhotos: JobPhoto[];
  currentSignatures: SignatureRecord[];
  checklistItems: ChecklistItem[];
  workItem: WorkItem | null;
  workflowState: WorkflowStateRecord | null;
  dispatchedRoute?: DispatchedRoute | null;
  /** Number of parts/materials/inventory items added to this job */
  partsCount?: number;
  /** Number of generated documents (inspection reports, etc.) for this job */
  documentsCount?: number;
  /** Number of assigned data validation templates that have not been completed yet */
  incompleteValidationCount?: number;
  /** Number of expenses recorded against the current work item */
  expenseCount?: number;
  /** Configured appointment subtype options, including per-type SmartTask rules */
  appointmentTypeOptions?: ConfigurableTypeOption[];
  /** Live GPS location of the assigned vehicle (from fleet tracking) */
  vehicleLocation?: { latitude: number; longitude: number } | null;
  /** Shift template geofence mode — used to break circular dependency with dynamic_job_site */
  geofenceMode?: string;
  /** When true, skip location/geofence/proximity enforcement (training sandbox) */
  isTrainingMode?: boolean;
  /** The selected completion resolution (resolved/partial/rescheduled/cancelled) — used to skip requirements that don't apply to the resolution */
  completionResolution?: CompletionResolution;
}

/**
 * Check if a specific action is allowed given current rules and context.
 * Returns ActionResult with blockedBy if action is not allowed.
 */
export function validateAction(
  action: WorkflowAction,
  rules: OperationalRules,
  context: RuleContext
): ActionResult {
  const {
    gpsLocation,
    gpsStatus,
    clockedIn,
    hasVehicle,
    hasInspectionToday,
    workItem,
    isTrainingMode,
  } = context;
  const currentPhotos = Array.isArray(context.currentPhotos) ? context.currentPhotos : [];
  const currentSignatures = Array.isArray(context.currentSignatures) ? context.currentSignatures : [];
  const checklistItems = Array.isArray(context.checklistItems) ? context.checklistItems : [];

  // Rule: Require clock-in for Smart Task
  // EXCEPTION: In dynamic_job_site mode, allow job-starting actions without clock-in
  // because clock-in requires an active job (breaks circular dependency).
  // Flow: start job in SmartTask → clock in near job site → SmartTask fully unlocks
  if (rules.requireClockInForSmartTask && !clockedIn && action !== 'select_vehicle') {
    const isDynamicJobSite = context.geofenceMode === 'dynamic_job_site';
    const isJobStartAction = ['check_in', 'check_in_without_travel', 'start_travel', 'arrive_site'].includes(action);
    if (isDynamicJobSite && isJobStartAction) {
      // Allow — user needs to start a job before they can clock in
      logger.debug(`[WorkflowEngine] Allowing ${action} without clock-in (dynamic_job_site mode)`);
    } else {
      return blocked('requireClockInForSmartTask', 'You must clock in before using Smart Task', 'Go to Smart Clock to clock in');
    }
  }

  switch (action) {
    case 'start_travel': {
      // Route order enforcement: must follow dispatched sequence
      if (rules.enforceRouteOrder && context.dispatchedRoute && context.workItem) {
        const stops = context.dispatchedRoute.stops || [];
        // Find the first pending stop in sequence
        const nextPendingStop = stops
          .filter(s => s.status === 'pending')
          .sort((a, b) => a.sequence - b.sequence)[0] as { sequence: number; job?: { id?: string; title?: string } } | undefined;
        if (nextPendingStop?.job?.id && nextPendingStop.job.id !== context.workItem.id) {
          return blocked('enforceRouteOrder',
            `You must follow the route order. Next stop: ${nextPendingStop.job.title || `Stop #${nextPendingStop.sequence}`}`,
            'Complete stops in the dispatched sequence');
        }
      }

      // Sequential job enforcement: block travel if another job is active
      // and allowMultipleJobsWithoutCheckout is disabled
      if (!rules.allowMultipleJobsWithoutCheckout && context.workflowState) {
        const ws = context.workflowState;
        const currentJobId = ws.active_work_item_id;
        // If there's an active job that isn't the one we're trying to travel to,
        // and the current state is not idle/ready (meaning we're mid-workflow),
        // block the action
        if (currentJobId && ws.current_state !== 'idle' && ws.current_state !== 'ready') {
          return blocked('allowMultipleJobsWithoutCheckout',
            'You must complete or check out of your current job before starting travel to another',
            'Go back and complete your current job first');
        }
      }
      // GPS required for travel?
      if (!isTrainingMode && rules.requireGPSForTravel && (!gpsLocation || gpsStatus !== 'ready')) {
        return blocked('requireGPSForTravel', 'GPS must be enabled before starting travel', 'Please enable location services');
      }
      // Vehicle required? (skippable if allowSkipVehicleRequirement is true and the user
      // clicked "No Vehicle / Public Transport" in BeginActionModal, which sets the
      // vehicle_requirement_skipped metadata flag)
      const vehicleSkipped = !!context.workflowState?.metadata?.vehicle_requirement_skipped
        && rules.allowSkipVehicleRequirement;
      if (rules.requireVehicle && !rules.isNonDriver && !hasVehicle && !vehicleSkipped) {
        return blocked('requireVehicle', 'You must select a vehicle before traveling', 'Select a vehicle from Fleet Management');
      }
      // Vehicle proximity check — user must be near the vehicle
      if (!isTrainingMode && rules.requireVehicleProximity && hasVehicle && gpsLocation && context.vehicleLocation) {
        const dist = haversineDistance(gpsLocation.latitude, gpsLocation.longitude, context.vehicleLocation.latitude, context.vehicleLocation.longitude);
        if (dist > rules.vehicleProximityRadiusMeters) {
          return blocked('requireVehicleProximity', `You must be within ${rules.vehicleProximityRadiusMeters}m of your vehicle (currently ${Math.round(dist)}m away)`, 'Move closer to the vehicle or check that the correct vehicle is selected');
        }
      }
      // ETA required?
      // (ETA is collected in the travel modal, not blocked here - just flagged)
      return success();
    }

    case 'check_in':
    case 'check_in_without_travel': {
      // GPS enforcement for check-in
      if (!isTrainingMode && rules.gpsEnforcement === 'strict' && (!gpsLocation || gpsStatus !== 'ready')) {
        return blocked('gpsEnforcement', 'GPS is required for check-in', 'Please enable location services');
      }
      if (!isTrainingMode && rules.gpsEnforcement === 'required' && (!gpsLocation || gpsStatus !== 'ready')) {
        return blocked('gpsEnforcement', 'GPS is required for check-in', 'Please enable location services');
      }

      // Per-appointment location verification (takes priority over global geofence)
      // If the appointment/work item has require_location_verification in custom_fields,
      // enforce geofence at the appointment's own radius against its own location.
      const appointmentRequiresVerification = workItem?.custom_fields?.require_location_verification === true;
      if (!isTrainingMode && appointmentRequiresVerification) {
        // Must have GPS to verify location — block if GPS unavailable
        if (!gpsLocation || gpsStatus !== 'ready') {
          return blocked('requireGeofenceForCheckin',
            'GPS is required to verify your location for this appointment',
            'Please enable location services and wait for a GPS fix');
        }
        const apptRadius = workItem?.custom_fields?.geofence_radius_meters
          ? Number(workItem.custom_fields.geofence_radius_meters)
          : rules.geofenceRadiusMeters || 100;
        const geoResult = checkGeofence(gpsLocation, workItem!, apptRadius);
        if (!geoResult.withinFence) {
          return blocked('requireGeofenceForCheckin',
            `You are ${Math.round(geoResult.distance)}m from the appointment location (must be within ${apptRadius}m)`,
            'Move closer to the appointment location to check in');
        }
      } else if (!isTrainingMode && rules.requireGeofenceForCheckin && workItem) {
        // Global geofence check (only if appointment doesn't have its own)
        // Must have GPS for geofence check
        if (!gpsLocation || gpsStatus !== 'ready') {
          return blocked('requireGeofenceForCheckin',
            'GPS is required to verify your location before check-in',
            'Please enable location services and wait for a GPS fix');
        }
        const geoResult = checkGeofence(gpsLocation, workItem, rules.geofenceRadiusMeters);
        if (!geoResult.withinFence) {
          return blocked('requireGeofenceForCheckin',
            `You are ${Math.round(geoResult.distance)}m from the job site (max ${rules.geofenceRadiusMeters}m)`,
            'Move closer to the job location');
        }
      }

      // Photo on start
      if (rules.requirePhotoOnStart && currentPhotos.filter(p => p.type === 'before').length === 0) {
        return blocked('requirePhotoOnStart', 'A photo must be taken before starting work', 'Take a "before" photo');
      }
      return success();
    }

    case 'complete_job': {
      // Appointment completion: only enforce appointment-specific requirements
      // (photos, signature, odometer). Job-level requirements like checklists,
      // parts, documents, and data validations are shared across all appointments
      // under a parent job and are only enforced when resolving the parent job.
      const isAppointmentCompletion = workItem?.item_type === 'appointment';
      const completionRulesOverride = isAppointmentCompletion
        ? getAppointmentCompletionRulesOverride(workItem)
        : null;
      if (completionRulesOverride) {
        return success({ completionRulesOverrideApplied: true });
      }
      const appointmentRequirementKeys = isAppointmentCompletion
        ? new Set(getAppointmentCompletionRequirementKeys(workItem, context.appointmentTypeOptions))
        : null;
      const requiresAppointmentPhotos = !isAppointmentCompletion
        ? rules.requirePhotoOnComplete
        : Boolean(appointmentRequirementKeys?.has('photos') && rules.requirePhotoOnComplete);
      const requiresAppointmentSignature = !isAppointmentCompletion
        ? rules.requireSignatureOnComplete
        : Boolean(appointmentRequirementKeys?.has('signature') && rules.requireSignatureOnComplete);
      const requiresAppointmentExpense = Boolean(appointmentRequirementKeys?.has('expenses'));
      // Signature is not required for resolutions that don't close out the visit (rescheduled/cancelled)
      const resolutionRequiresSignature = requiresSignatureForCompletionResolution(context.completionResolution ?? 'resolved');
      // Photos are not required for resolutions that don't close out the visit (rescheduled/cancelled)
      const resolutionRequiresPhotos = requiresPhotosForCompletionResolution(context.completionResolution ?? 'resolved');

      // Photo on complete — any non-'before' photo counts as completion evidence
      if (requiresAppointmentPhotos && resolutionRequiresPhotos && currentPhotos.filter(p => p.type === 'after' || p.type === 'proof' || p.type === 'general' || p.type === 'during').length === 0) {
        return blocked('requirePhotoOnComplete', 'Proof photos are required before completing', 'Take completion photos');
      }
      // Signature on complete — skip for rescheduled / cancelled resolutions
      if (requiresAppointmentSignature && resolutionRequiresSignature && currentSignatures.length === 0) {
        return blocked('requireSignatureOnComplete', 'A signature is required before completing', 'Capture customer signature');
      }
      // Expense appointments must include at least one expense before completion
      if (requiresAppointmentExpense && (context.expenseCount ?? 0) === 0) {
        return blocked('requireExpenseBeforeComplete' as any, 'An expense is required before completing this appointment', 'Record at least one expense from the Expenses tab');
      }

      // Job-level requirements — skip for appointment completions
      if (!isAppointmentCompletion) {
        // Parts/inventory enforcement
        if (rules.requirePartsBeforeComplete && (context.partsCount ?? 0) === 0) {
          return blocked('requirePartsBeforeComplete' as any, 'At least one billing item or service item must be added before completing', 'Add billing items, materials, or service items to this job');
        }
        // Document/inspection enforcement
        if (rules.requireDocumentBeforeComplete && (context.documentsCount ?? 0) === 0) {
          return blocked('requireDocumentBeforeComplete' as any, 'An inspection document or report must be generated before completing', 'Generate a document from the Documents action');
        }
        // Checklist complete
        if (rules.requireChecklistComplete) {
          const requiredItems = checklistItems.filter(c => c.required);
          const incomplete = requiredItems.filter(c => !c.completed);
          if (incomplete.length > 0) {
            return blocked('requireChecklistComplete',
              `${incomplete.length} required checklist items not completed`,
              'Complete all required checklist items');
          }
        }
        // Data validation templates must be completed
        if (rules.requireDataValidationComplete && (context.incompleteValidationCount ?? 0) > 0) {
          return blocked('requireDataValidationComplete' as any,
            `${context.incompleteValidationCount} data validation template${context.incompleteValidationCount === 1 ? '' : 's'} not completed`,
            'Complete all assigned data validations from the Work Breakdown tab or SmartTask actions');
        }
      }
      return success();
    }

    case 'cancel_inspection':
      return success(); // Always allowed — escape hatch from stuck inspection_pending

    case 'select_vehicle': {
      if (rules.isNonDriver) {
        return blocked(
          'requireVehicle',
          'Non-driver mode disables vehicle selection for this worker',
          'Ask an admin to turn off Non-Driver Mode before assigning a vehicle'
        );
      }
      // Vehicle proximity check — user must be near the vehicle when selecting it
      if (rules.requireVehicleProximity && gpsLocation && context.vehicleLocation) {
        const dist = haversineDistance(gpsLocation.latitude, gpsLocation.longitude, context.vehicleLocation.latitude, context.vehicleLocation.longitude);
        if (dist > rules.vehicleProximityRadiusMeters) {
          return blocked('requireVehicleProximity', `You must be within ${rules.vehicleProximityRadiusMeters}m of the vehicle (currently ${Math.round(dist)}m away)`, 'Move closer to the vehicle before selecting it');
        }
      }
      return success();
    }

    case 'complete_inspection': {
      if (rules.isNonDriver) {
        return blocked(
          'requireTakeOnInspection',
          'Non-driver mode disables vehicle inspections for this worker',
          'Ask an admin to turn off Non-Driver Mode before requiring vehicle inspections'
        );
      }
      // Vehicle proximity check — user must be near the vehicle during inspection
      if (rules.requireVehicleProximity && gpsLocation && context.vehicleLocation) {
        const dist = haversineDistance(gpsLocation.latitude, gpsLocation.longitude, context.vehicleLocation.latitude, context.vehicleLocation.longitude);
        if (dist > rules.vehicleProximityRadiusMeters) {
          return blocked('requireVehicleProximity', `You must be within ${rules.vehicleProximityRadiusMeters}m of the vehicle to complete inspection (currently ${Math.round(dist)}m away)`, 'Move closer to the vehicle');
        }
      }
      return success(); // Inspection form handles its own validation
    }

    case 'arrive_site': {
      if (!isTrainingMode && rules.requireArrivalPhoto && currentPhotos.filter(p => p.type === 'proof').length === 0) {
        return blocked(
          'requireArrivalPhoto',
          'A photo must be taken before marking arrival',
          'Take a proof photo to confirm your arrival at the site'
        );
      }
      return success();
    }

    default:
      return success();
  }
}

export function shouldRequireVehicleInspection(
  rules: OperationalRules,
  context: Pick<RuleContext, 'hasInspectionToday' | 'hasInspectionForVehicle'> & {
    /**
     * Collective gate: evaluate the inspection rules for a non-driver too.
     * Used by isInspectionEnforcementRequired — a shared vehicle must not
     * travel uninspected regardless of which assignee tapped Start Travel.
     * The state machine (determineNextState/getInitialStateForJob) does NOT
     * pass this: non-drivers never enter inspection_pending, a state they
     * cannot resolve themselves.
     */
    nonDriverCollectiveGate?: boolean;
  },
): boolean {
  if (rules.isNonDriver && !context.nonDriverCollectiveGate) {
    return false;
  }

  // Rule 1: Daily pre-trip inspection — once per day
  if (rules.requireDailyPreTripInspection && !context.hasInspectionToday) {
    return true;
  }

  // Rule 2: Take-on inspection — when claiming/changing vehicle
  const requireTakeOn = rules.requireTakeOnInspection;
  if (
    requireTakeOn
    && !rules.requireDailyPreTripInspection
    && !context.hasInspectionForVehicle
  ) {
    return true;
  }

  // Rule 3: Handover inspection — incoming + outgoing with QR pairing
  // Behaves like take-on for gating purposes (requires inspection on vehicle change)
  if (
    rules.requireHandoverInspection
    && !rules.requireDailyPreTripInspection
    && !context.hasInspectionForVehicle
  ) {
    return true;
  }

  return false;
}

/**
 * Check if state transition is valid
 */
export function canTransition(from: WorkflowState, to: WorkflowState): boolean {
  return VALID_TRANSITIONS[from]?.includes(to) ?? false;
}

/**
 * Get available actions for current state + rules
 */
export function getAvailableActions(
  state: WorkflowState,
  rules: OperationalRules,
  context: RuleContext
): { action: WorkflowAction; enabled: boolean; blockedReason?: string }[] {
  const stateActions = STATE_ACTIONS[state] || [];
  return stateActions.map(action => {
    const result = validateAction(action, rules, context);
    return {
      action,
      enabled: result.success,
      blockedReason: result.blockedBy?.message,
    };
  });
}

/**
 * Determine the next workflow state based on current state and config
 */
export function determineNextState(
  currentState: WorkflowState,
  action: WorkflowAction,
  rules: OperationalRules,
  context: RuleContext
): WorkflowState | null {
  switch (action) {
    case 'select_vehicle': {
      if (rules.isNonDriver) {
        return 'ready';
      }
      if (shouldRequireVehicleInspection(rules, context)) {
        return rules.allowInspectionRemindLater ? 'ready' : 'inspection_pending';
      }
      return 'ready';
    }

    case 'complete_inspection':
      return 'ready';

    case 'cancel_inspection':
      return 'idle';

    case 'start_travel':
      return 'traveling';

    case 'pause_travel':
      return 'travel_paused';

    case 'resume_travel':
      return 'traveling';

    case 'cancel_travel':
      return 'ready';

    case 'arrive_site':
      return 'arrived';

    case 'check_in':
    case 'check_in_without_travel':
      return 'checked_in';

    case 'pause_work':
      return 'paused';

    case 'resume_work':
      return 'checked_in';

    case 'start_completing':
      return 'completing';

    case 'complete_job':
      return 'idle';

    default:
      return null; // Action doesn't change state (photo, signature, etc.)
  }
}

/**
 * Get the initial state based on rules when selecting a new job
 * 
 * IMPORTANT: Prevents vehicle_required state during working phases
 * to avoid late-stage blocking. Vehicle selection must happen BEFORE
 * starting work, not during or after.
 */
export function getInitialStateForJob(
  rules: OperationalRules,
  context: RuleContext,
  currentState?: WorkflowState,
): WorkflowState {
  // Prevent transitioning to vehicle_required if already working/completing
  // Vehicle selection gates should fail fast at workflow start, not mid-workflow
  const workingStates = new Set(['checked_in', 'paused', 'completing', 'working']);
  const isAlreadyWorking = currentState && workingStates.has(currentState);

  // If vehicle is required and none selected
  // BUT not while already working (that would be a late-stage block)
  // AND not if the user skipped vehicle selection (public transport / no-vehicle scenario)
  const vehicleSkipped = !!context.workflowState?.metadata?.vehicle_requirement_skipped
    && rules.allowSkipVehicleRequirement;
  if (rules.requireVehicle && !rules.isNonDriver && !context.hasVehicle && !isAlreadyWorking && !vehicleSkipped) {
    return 'vehicle_required';
  }
  // Inspection can either block workflow or surface as a reminder-only policy.
  if (shouldRequireVehicleInspection(rules, context)) {
    return rules.allowInspectionRemindLater ? 'ready' : 'inspection_pending';
  }
  // If travel is required
  if (rules.requireTravel) {
    return 'ready';
  }
  // Ready to check in directly
  return 'ready';
}

/**
 * Get the completion requirements for current job
 */
export function getCompletionRequirements(
  rules: OperationalRules,
  context: RuleContext
): CompletionRequirementState[] {
  const reqs: CompletionRequirementState[] = [];
  const currentPhotos = Array.isArray(context.currentPhotos) ? context.currentPhotos : [];
  const currentSignatures = Array.isArray(context.currentSignatures) ? context.currentSignatures : [];
  const checklistItems = Array.isArray(context.checklistItems) ? context.checklistItems : [];
  const completionRulesOverride = getAppointmentCompletionRulesOverride(context.workItem);
  const appointmentRequirementKeys = context.workItem?.item_type === 'appointment'
    ? new Set(getAppointmentCompletionRequirementKeys(context.workItem, context.appointmentTypeOptions))
    : null;
  const knownAppointmentRequirementKeys = new Set<AppointmentCompletionRequirementKey>([
    'photos',
    'signature',
    'odometer',
    'expenses',
    'public_comments',
  ]);

  const getRequirementOverride = (requirementKey: string) => {
    if (context.workItem?.item_type !== 'appointment') {
      return null;
    }

    if (!knownAppointmentRequirementKeys.has(requirementKey as AppointmentCompletionRequirementKey)) {
      return null;
    }

    return getAppointmentCompletionRequirementOverride(
      context.workItem,
      requirementKey as AppointmentCompletionRequirementKey,
      context.appointmentTypeOptions
    );
  };

  const pushRequirement = (requirement: CompletionRequirementState) => {
    if (completionRulesOverride) {
      reqs.push({
        ...requirement,
        met: true,
        overridden: true,
        overrideReason: completionRulesOverride.reason || null,
        label: `${requirement.label} — override approved`,
      });
      return;
    }

    const override = getRequirementOverride(requirement.key);
    if (!override) {
      reqs.push(requirement);
      return;
    }

    reqs.push({
      ...requirement,
      met: true,
      overridden: true,
      overrideReason: override.reason || null,
      label: `${requirement.label} — override approved`,
    });
  };

  if (rules.requirePhotoOnComplete && (!appointmentRequirementKeys || appointmentRequirementKeys.has('photos')) && requiresPhotosForCompletionResolution(context.completionResolution ?? 'resolved')) {
    const hasProofPhotos = currentPhotos.some(p => p.type === 'after' || p.type === 'proof' || p.type === 'general' || p.type === 'during');
    pushRequirement({ key: 'photos', label: 'Completion photos', met: hasProofPhotos, required: true });
  }

  if (rules.requireSignatureOnComplete && (!appointmentRequirementKeys || appointmentRequirementKeys.has('signature'))) {
    pushRequirement({ key: 'signature', label: 'Customer signature', met: currentSignatures.length > 0, required: true });
  }

  if (rules.requireChecklistComplete) {
    const required = checklistItems.filter(c => c.required);
    const allDone = required.every(c => c.completed);
    pushRequirement({ key: 'checklist', label: `Checklist (${required.filter(c => c.completed).length}/${required.length})`, met: allDone, required: true });
  }

  // Odometer reading no longer enforced on completion


  if (appointmentRequirementKeys?.has('expenses')) {
    const expenseCount = context.expenseCount ?? 0;
    pushRequirement({
      key: 'expenses',
      label: `Expense${expenseCount > 0 ? ` (${expenseCount})` : ''}`,
      met: expenseCount > 0,
      required: true,
    });
  }

  if (rules.requirePartsBeforeComplete) {
    const hasParts = (context.partsCount ?? 0) > 0;
    pushRequirement({ key: 'parts', label: 'Billing items / service items added', met: hasParts, required: true });
  }

  if (rules.requireDocumentBeforeComplete) {
    const hasDocs = (context.documentsCount ?? 0) > 0;
    pushRequirement({ key: 'documents', label: 'Inspection / report document generated', met: hasDocs, required: true });
  }

  if (rules.requireDataValidationComplete) {
    const incompleteCount = context.incompleteValidationCount ?? 0;
    // Only show this requirement if there are actually assigned validations
    if (incompleteCount > 0) {
      pushRequirement({ key: 'validations', label: `Data validations (${incompleteCount} incomplete)`, met: false, required: true });
    }
  }

  return reqs;
}

// ============================================================================
// HELPERS
// ============================================================================

function checkGeofence(location: GPSLocation, workItem: WorkItem, radiusMeters: number): { withinFence: boolean; distance: number } {
  if (!workItem.location_lat || !workItem.location_lng) {
    return { withinFence: true, distance: 0 }; // No location on job = skip geofence
  }
  const distance = calculateDistanceMeters(
    location.latitude, location.longitude,
    workItem.location_lat, workItem.location_lng
  );
  return { withinFence: distance <= radiusMeters, distance };
}

function calculateDistanceMeters(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371000; // Earth's radius in meters
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function toRad(deg: number): number {
  return deg * (Math.PI / 180);
}

function blocked(rule: keyof OperationalRules, message: string, remediation?: string): ActionResult {
  return { success: false, blockedBy: { rule, message, remediation } };
}

function success(data?: Record<string, any>): ActionResult {
  return { success: true, data };
}
